import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import { useSelector } from 'react-redux'
import urls from '../../constants/apiurl'
import ApiCaller from '../../constants/ApiCaller'
import { showDangerToast, showToast } from '../../constants/toaster'
import './SendUserNotificationModal.css'

const PRIMARY = '#25D366'

const buildDisplayName = (fullName, userName) => {
  const name = (fullName || '').trim()
  const username = (userName || '').trim()
  if (name && username) return `${name} (${username})`
  if (name) return name
  if (username) return username
  return 'User'
}

export const SendUserWhatsAppModal = ({
  show,
  onHide,
  mode = 'single',
  userId,
  fullName,
  userName,
  phoneNumber
}) => {
  const userData = useSelector(state => state?.userData)
  const [displayName, setDisplayName] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [errors, setErrors] = useState({ displayNameError: '', messageError: '' })
  const [sending, setSending] = useState(false)

  const isBulk = mode === 'bulk'

  useEffect(() => {
    if (!show) return
    if (!isBulk) {
      setDisplayName(buildDisplayName(fullName, userName))
    } else {
      setDisplayName('')
    }
    setCustomMessage('')
    setErrors({ displayNameError: '', messageError: '' })
  }, [show, isBulk, fullName, userName])

  const resetForm = () => {
    setDisplayName(isBulk ? '' : buildDisplayName(fullName, userName))
    setCustomMessage('')
    setErrors({ displayNameError: '', messageError: '' })
  }

  const handleClose = () => {
    if (sending) return
    resetForm()
    onHide()
  }

  const validateFields = () => {
    const tempErrors = { displayNameError: '', messageError: '' }
    let isValid = true

    if (!isBulk && !displayName.trim()) {
      tempErrors.displayNameError = 'Recipient name is required.'
      isValid = false
    }
    if (!customMessage.trim()) {
      tempErrors.messageError = 'Custom message is required.'
      isValid = false
    }

    setErrors(tempErrors)
    return isValid
  }

  const sendWhatsAppMessage = async () => {
    if (!isBulk && !userId) {
      showDangerToast('User id is missing.')
      return
    }
    if (!validateFields()) return

    try {
      setSending(true)
      const postJson = isBulk
        ? {
            all: true,
            customMessage: customMessage.trim()
          }
        : {
            userId: String(userId),
            displayName: displayName.trim(),
            customMessage: customMessage.trim()
          }

      const res = await ApiCaller.post(
        postJson,
        urls.sendAdminWhatsApp,
        userData?.access_token
      )
      const response = await res.response.json()

      if (response.status == 200) {
        showToast(
          response?.message ||
            (isBulk
              ? 'WhatsApp messages are being sent to all users.'
              : 'WhatsApp message sent successfully.')
        )
        resetForm()
        onHide()
      } else {
        showDangerToast(response.message || 'Failed to send WhatsApp message.')
      }
    } catch (error) {
      console.error(error)
      showDangerToast(error.message || 'Failed to send WhatsApp message.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="send-notification-dialog"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 0 2rem rgba(136, 152, 170, 0.2)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          className="send-notification-dialog__header"
          sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%) !important' }}
        >
          <Box display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
            <Box className="send-notification-dialog__header-icon">
              <WhatsAppIcon />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Send WhatsApp
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {isBulk ? 'Message all users with phone numbers' : 'Message one user'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={sending}
            size="small"
            sx={{ color: 'inherit' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
        {!isBulk && (
          <Box className="send-notification-dialog__recipient" sx={{ mb: 2.5 }}>
            <PersonIcon sx={{ fontSize: 20, color: PRIMARY }} />
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Recipient
              </Typography>
              <Chip
                label={displayName || 'User'}
                size="small"
                sx={{
                  mt: 0.5,
                  fontWeight: 600,
                  bgcolor: `${PRIMARY}14`,
                  color: PRIMARY,
                  maxWidth: '100%',
                  height: 'auto',
                  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 }
                }}
              />
              {phoneNumber ? (
                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {phoneNumber}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        )}

        <Stack spacing={2}>
          {!isBulk && (
            <TextField
              label="Full name / Username"
              placeholder="Full name (username)"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              disabled={sending}
              fullWidth
              error={Boolean(errors.displayNameError)}
              helperText={errors.displayNameError || 'Used as {{1}} in the WhatsApp template'}
            />
          )}

          <TextField
            label="Custom message"
            placeholder="Enter the update message for the user"
            value={customMessage}
            onChange={e => setCustomMessage(e.target.value)}
            disabled={sending}
            fullWidth
            multiline
            minRows={4}
            error={Boolean(errors.messageError)}
            helperText={
              errors.messageError ||
              (isBulk
                ? 'Each user will receive their own name with this message as {{2}}'
                : 'Used as {{2}} in the WhatsApp template')
            }
          />

          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: '#f8f9fe',
              border: '1px solid #e9ecef'
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Template preview
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-line' }}>
              {`Hello ${isBulk ? '{{name}}' : displayName || '{{1}}'},\nHere is an important update regarding your account:\n${customMessage || '{{2}}'}\nThank you for choosing Praxo.\nPraxo Support Team`}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: '1px solid #e9ecef',
          bgcolor: '#f8f9fe'
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={sending}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e9ecef',
            color: '#525f7f'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={sendWhatsAppMessage}
          disabled={sending}
          startIcon={<WhatsAppIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: PRIMARY,
            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.35)',
            '&:hover': { bgcolor: '#1ebe57' }
          }}
        >
          {sending ? 'Sending…' : isBulk ? 'Send to all users' : 'Send WhatsApp'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
